import { Directive, ElementRef, HostListener, Renderer2, AfterViewChecked, inject, effect, input, computed } from '@angular/core';

import { ApWindowService } from '@ap/cdk';

/**
 * @module AutoGrowModule
 *
 * @ngdoc directive
 * @name ApAutoGrowDirective
 *
 * @param {number=} apAutoGrow | Number of rows after which the element won't resize but
 * display a inner scrollbar instead.
 */
@Directive({ selector: '[apAutoGrow]' })
export class ApAutoGrowDirective implements AfterViewChecked {
    private readonly elementRef = inject(ElementRef);
    private readonly renderer = inject(Renderer2);
    private readonly windowService = inject(ApWindowService);

    /**
     * Maximum amount of rows to grow to
     */
    public readonly apAutoGrow = input.required<number>();

    public nativeElement: any;
    private lineHeight: number;
    private paddingBottom: number;
    private minRows = 4;

    /**
     * Is autogrow enabled
     */
    private readonly enabled = computed(() => {
        const maxRows = this.apAutoGrow();
        return maxRows > this.minRows;
    });

    constructor() {
        this.nativeElement = this.elementRef.nativeElement;
        effect(() => {
            const maxRows = this.apAutoGrow();
            if (maxRows === undefined) {
                return;
            }
            this.setAutoGrowth(maxRows);
        });
    }

    @HostListener('input')
    public onInput(): void {
        this.enabled() && this.adjustGrowth();
    }

    public ngAfterViewChecked(): void {
        if (this.enabled()) {
            const textAreaComputedStyle = this.windowService.getWindow().getComputedStyle(this.nativeElement);
            const textAreaLineHeightVal = textAreaComputedStyle.getPropertyValue('line-height');
            this.paddingBottom = parseInt(textAreaComputedStyle.getPropertyValue('padding-bottom'), 10);
            this.lineHeight = parseInt(textAreaLineHeightVal.slice(0, -2), 10);
            this.adjustGrowth();
        }
    }

    public adjustGrowth(): void {
        const { nativeElement } = this;
        const height = Math.round(nativeElement.scrollHeight / this.lineHeight);

        if (height > nativeElement.maxRows) {
            const maxHeight = this.nativeElement.maxRows * this.lineHeight + this.paddingBottom;
            this.nativeElement.style.height = `${maxHeight}px`;
        } else {
            this.nativeElement.style.removeProperty('height'); // Necessary for resetting height
            this.nativeElement.style.height = `${this.nativeElement.scrollHeight}px`;
        }
    }

    private setAutoGrowth(maxRows: number): void {
        if (this.enabled()) {
            // we need angular renderer to make sure the attr will be there
            this.renderer.setAttribute(this.nativeElement, 'max-rows', maxRows.toString());
            this.nativeElement.maxRows = maxRows;
            this.nativeElement.style.resize = 'none';
        } else {
            this.renderer.removeAttribute(this.nativeElement, 'max-rows');
            this.nativeElement.removeAttribute('max-rows');
            ['resize', 'height', 'overflow-y'].forEach((prop) => this.nativeElement.style.removeProperty(prop));
        }
    }
}
